/* The live-session join protocol (SessionJoinPage), as the console speaks it.
 *
 *   URL      GET {socket}/api/v1/sessions/:id/join?mode=observer|peer&token=…&tenant…
 *   server → {"type":"output","data": string | number[]}   terminal bytes
 *            {"type":"participant_join"|"participant_leave", …}
 *            anything that is not JSON is terminal text
 *   client → peer mode only: a text frame per xterm onData sequence, as-is
 *            (keys, Enter "\r", Backspace "\x7f", Tab, arrows "\x1b[A", paste)
 *
 * The server side of this protocol is not in this repo: the shapes above are
 * what the page has always parsed and sent. Resize is not sent: a joiner does
 * not own the PTY size. */

export type JoinMode = 'observer' | 'peer';

export function parseJoinMode(value: string | null | undefined): JoinMode {
    return value === 'peer' ? 'peer' : 'observer';
}

/** The join socket URL, every value encoded (design eval F14: the token was
 *  appended raw). */
export function joinSocketUrl(socketBase: string, sessionId: string, mode: JoinMode, token: string | undefined, tenantParam: string): string {
    const params = [`mode=${encodeURIComponent(mode)}`];
    // F14, backend decision: the token rides in the query string because a
    // browser WebSocket cannot set headers. Whether it moves to an HttpOnly
    // cookie or a short-lived ticket is for the server to decide.
    if (token) params.push(`token=${encodeURIComponent(token)}`);
    if (tenantParam) params.push(tenantParam);
    return `${socketBase}/api/v1/sessions/${encodeURIComponent(sessionId)}/join?${params.join('&')}`;
}

/** Minimal socket shape, so the helper is testable without a real WebSocket. */
export interface SendableSocket { readyState: number; send(data: string): void; }
const OPEN = 1; // WebSocket.OPEN

/** Forward one xterm onData sequence to the session. Only a peer types; an
 *  observer's keys go nowhere. Returns whether it was sent. */
export function forwardPeerInput(mode: JoinMode, ws: SendableSocket | null | undefined, data: string): boolean {
    if (mode !== 'peer' || !ws || ws.readyState !== OPEN || !data) return false;
    ws.send(data);
    return true;
}

export type JoinMessage =
    | { kind: 'output'; text: string }
    | { kind: 'participants' }
    | { kind: 'other' };

/** Decode one server frame. */
export function decodeJoinMessage(raw: unknown): JoinMessage {
    if (typeof raw !== 'string') return { kind: 'other' };
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return { kind: 'output', text: raw };
    }
    if (!parsed || typeof parsed !== 'object') return { kind: 'output', text: raw };
    const msg = parsed as { type?: unknown; data?: unknown };
    if (msg.type === 'output') {
        const d = msg.data;
        const text = typeof d === 'string' ? d : Array.isArray(d) ? new TextDecoder().decode(new Uint8Array(d)) : '';
        return { kind: 'output', text };
    }
    if (msg.type === 'participant_join' || msg.type === 'participant_leave') return { kind: 'participants' };
    return { kind: 'other' };
}
